import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound, Outlet, useRouterState } from '@tanstack/react-router'
import { LockKeyholeIcon } from 'lucide-react'
import { useRef } from 'react'

import { ApiError } from '@/api/errors'
import { OpenBook } from '@/components/corporate/open-book'
import { PageHeader } from '@/components/page-header'
import { CORPORATION_QUERY } from '@/hooks/use-corporate-access'
import { SLIDING_ROW_CLASS, useSlidingRow } from '@/hooks/use-sliding-row'
import { cn } from '@/lib/utils'

/**
 * Le livre des minutes (lot 45, contrat 1.88.0) : une section privée, pour ses
 * gardiens seulement. Pour tout autre compte, l'adresse n'existe pas — la même
 * page « introuvable » qu'une route inconnue, jamais un « accès refusé » qui
 * dirait qu'il y a quelque chose ici.
 */
export const Route = createFileRoute('/_app/corporate')({
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.query({ ...CORPORATION_QUERY, retry: false })
    } catch (error) {
      // `notFound()` est le signal du routeur pour sa page « introuvable », pas une Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      if (error instanceof ApiError && error.isNotFound) throw notFound()
      throw error
    }
  },
  component: CorporateLayout,
})

const SECTIONS = [
  { to: '/corporate', label: 'Tableau de bord', exact: true },
  { to: '/corporate/registres', label: 'Registres' },
  { to: '/corporate/resolutions', label: 'Résolutions' },
  { to: '/corporate/pieces', label: 'Pièces' },
  { to: '/corporate/calendrier', label: 'Calendrier' },
  { to: '/corporate/exporter', label: 'Exporter' },
  { to: '/corporate/journal', label: 'Journal' },
] as const

function CorporateLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const corporation = useQuery({ ...CORPORATION_QUERY, retry: false })
  const navRef = useRef<HTMLElement>(null)
  const edges = useSlidingRow(navRef, '[aria-current="page"]', [pathname])

  // Un administrateur sans livre ouvert : on l'ouvre ici, une seule fois.
  if (corporation.data === null) return <OpenBook />
  const data = corporation.data

  return (
    <div className="space-y-6" data-testid="livre-des-minutes">
      <PageHeader
        title="Livre des minutes"
        description={
          data
            ? `${data.legal_name} · n° ${data.corporation_number} · Nouveau-Brunswick`
            : undefined
        }
      />
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <LockKeyholeIcon className="size-4 shrink-0" aria-hidden="true" />
        Section privée : seuls les gardiens du livre la voient. Rien ne s’y supprime ; le livre se
        garde jusqu’à deux ans après la dissolution de la société.
      </p>
      <nav
        ref={navRef}
        aria-label="Sections du livre des minutes"
        data-edges={edges}
        className={cn('flex gap-1 border-b border-border', SLIDING_ROW_CLASS)}
      >
        {SECTIONS.map((section) => {
          const active =
            'exact' in section
              ? pathname === section.to || pathname === `${section.to}/`
              : pathname === section.to || pathname.startsWith(`${section.to}/`)
          return (
            <Link
              key={section.to}
              to={section.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative inline-flex h-9 shrink-0 items-center rounded-t-md px-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                'after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full',
                active
                  ? 'text-foreground after:bg-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {section.label}
            </Link>
          )
        })}
      </nav>
      <Outlet />
    </div>
  )
}
